import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeftIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { normalizeAuthReturnTo, pendingAuthPrompt } from "@/lib/contextualAuth";
import { registrationPasswordError } from "@/lib/registration";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { trackEvent } from "@/lib/analytics";

const HERO = "https://images.unsplash.com/photo-1506316940527-4d1c138978a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const GOOGLE_SIGN_IN_ENABLED = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim());

function AuthMethodDivider() {
  if (!GOOGLE_SIGN_IN_ENABLED) return null;
  return (
    <div className="my-5 flex items-center gap-3" aria-hidden="true">
      <span className="h-px flex-1 bg-white/10" />
      <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">{translateLabel("or use email")}</span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
  );
}

export function AuthShell({ title, subtitle, children }) {
  const navigate = useNavigate();
  const { pendingAction, cancelPendingAction } = useAuth();

  const handleBack = () => {
    const returnTo = pendingAction?.returnTo || "/";
    cancelPendingAction();
    navigate(returnTo);
  };

  return (
    <div className="flex min-h-screen bg-[#09090b]">
      <div className="relative hidden w-1/2 lg:block">
        <img src={HERO} alt="MTB" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#09090b] via-[#09090b]/50 to-transparent" />
        <div className="absolute bottom-12 left-12 max-w-md">
          <h2 className="font-display text-5xl font-black uppercase leading-none text-white">{translateLabel("Find your")}<br /><span className="text-[#CCFF00]">{translateLabel("next ride.")}</span></h2>
          <p className="mt-4 text-zinc-300">{translateLabel("Save bike parks and trails, build your Want to Ride list and never wonder where to ride again.")}</p>
        </div>
      </div>
      <div className="flex w-full flex-col justify-center px-6 py-12 lg:w-1/2 lg:px-16">
        <button type="button" onClick={handleBack} className="mb-8 flex items-center gap-2 self-start text-sm font-bold text-zinc-400 transition-colors hover:text-white" data-testid="auth-back">
          <ArrowLeftIcon size={16} weight="bold" /> {pendingAction ? translateLabel("Back to your ride") : translateLabel("Back to discovery")}
        </button>
        <img
          src="/images/logo/logo-tr-medium.png"
          srcSet="/images/logo/logo-tr-small.png 128w, /images/logo/logo-tr-medium.png 512w, /images/logo/logo-tr-big.png 1536w"
          sizes="(min-width: 1024px) 320px, 240px"
          alt="Bikeparks Finder"
          className="h-24 w-auto max-w-full object-contain object-left"
        />
        <h1 className="mt-8 font-display text-4xl font-black uppercase text-white">{title}</h1>
        <p className="mt-2 text-zinc-400">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="label-tiny">{label}</span>
      <input
        {...props}
        className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-zinc-500 focus:border-[#CCFF00] focus:outline-none focus:ring-1 focus:ring-[#CCFF00]"
      />
    </label>
  );
}

export function Login() {
  const { login, googleLogin, pendingAction, resumePendingAction, authenticationFailed } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const finishAuthentication = async (defaultMessage) => {
    const fallbackReturnTo = pendingAction?.returnTo || normalizeAuthReturnTo(searchParams.get("returnTo"));
    try {
      const resumed = await resumePendingAction();
      toast.success(resumed.resumed ? "Signed in. Continuing where you left off." : defaultMessage);
      navigate(resumed.resumed ? resumed.returnTo : fallbackReturnTo);
    } catch {
      toast.error(translateLabel("Signed in, but the requested action could not be completed."));
      navigate(fallbackReturnTo);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      trackEvent("login", { method: "email" });
      await finishAuthentication("Welcome back!");
    } catch (e) {
      authenticationFailed();
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const submitGoogle = async (credential) => {
    setErr(""); setBusy(true);
    try {
      await googleLogin(credential);
      trackEvent("login", { method: "google" });
      await finishAuthentication("Signed in with Google!");
    } catch (e) {
      authenticationFailed();
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title={translateLabel("Sign in")} subtitle={pendingAuthPrompt(pendingAction, "login") || translateLabel("Welcome back, rider.")}>
      {GOOGLE_SIGN_IN_ENABLED && <GoogleSignInButton onCredential={submitGoogle} disabled={busy} />}
      <AuthMethodDivider />
      <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className={`${GOOGLE_SIGN_IN_ENABLED ? "mt-0" : "mt-8"} space-y-4`} data-testid="login-form">
        <Field label={translateLabel("Email")} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="login-email" />
        <Field label={translateLabel("Password")} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" />
        {err && <p className="text-sm font-semibold text-[#FF4500]" data-testid="login-error">{err}</p>}
        <button type="submit" disabled={busy} className="w-full rounded-full bg-[#CCFF00] py-3.5 font-bold text-black transition-transform active:scale-95 disabled:opacity-60" data-testid="login-submit">
          {busy ? translateLabel("Signing in…") : translateLabel("Sign in")}
        </button>
      </motion.form>
      <p className="mt-6 text-sm text-zinc-400">{translateLabel("No account?")} <Link to={searchParams.get("returnTo") ? `/register?returnTo=${encodeURIComponent(normalizeAuthReturnTo(searchParams.get("returnTo")))}` : "/register"} className="font-bold text-[#CCFF00] hover:underline" data-testid="go-register">{translateLabel("Create one")}</Link></p>
    </AuthShell>
  );
}

export function Register() {
  const { register, googleLogin, pendingAction, resumePendingAction, authenticationFailed } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const finishRegistration = async (defaultMessage) => {
    const fallbackReturnTo = pendingAction?.returnTo || normalizeAuthReturnTo(searchParams.get("returnTo"));
    try {
      const resumed = await resumePendingAction();
      toast.success(resumed.resumed ? "Account created. Continuing where you left off." : defaultMessage);
      navigate(resumed.resumed ? resumed.returnTo : fallbackReturnTo);
    } catch {
      toast.error(translateLabel("Account created, but the requested action could not be completed."));
      navigate(fallbackReturnTo);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const passwordError = registrationPasswordError(password, confirmPassword);
    if (passwordError) {
      setErr(passwordError);
      return;
    }

    setErr(""); setBusy(true);
    try {
      const registration = await register(name, email, password);
      trackEvent("sign_up", { method: "email" });
      await finishRegistration(
        registration.verification_email_sent
          ? "Account created. Check your email to verify it."
          : "Account created!",
      );
    } catch (e) {
      authenticationFailed();
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const submitGoogle = async (credential) => {
    setErr(""); setBusy(true);
    try {
      await googleLogin(credential);
      trackEvent("sign_up", { method: "google" });
      await finishRegistration("Account ready. Signed in with Google!");
    } catch (e) {
      authenticationFailed();
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title={translateLabel("Create account")} subtitle={pendingAuthPrompt(pendingAction, "register") || translateLabel("Start saving your rides in seconds.")}>
      {GOOGLE_SIGN_IN_ENABLED && <GoogleSignInButton onCredential={submitGoogle} disabled={busy} />}
      <AuthMethodDivider />
      <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className={`${GOOGLE_SIGN_IN_ENABLED ? "mt-0" : "mt-8"} space-y-4`} data-testid="register-form">
        <Field label={translateLabel("Name")} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} placeholder={translateLabel("Your name")} required data-testid="register-name" />
        <Field label={translateLabel("Email")} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="register-email" />
        <Field label={translateLabel("Password")} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={translateLabel("At least 8 characters")} minLength={8} required data-testid="register-password" />
        <Field label={translateLabel("Confirm password")} type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={translateLabel("Repeat your password")} minLength={8} required data-testid="register-confirm-password" />
        <p className="text-xs leading-relaxed text-zinc-500">{translateLabel("Rider level, riding style and other preferences stay optional and can be added later in your Rider Profile.")}</p>
        {err && <p className="text-sm font-semibold text-[#FF4500]" data-testid="register-error">{err}</p>}
        <button type="submit" disabled={busy} className="w-full rounded-full bg-[#CCFF00] py-3.5 font-bold text-black transition-transform active:scale-95 disabled:opacity-60" data-testid="register-submit">
          {busy ? translateLabel("Creating…") : translateLabel("Create account")}
        </button>
      </motion.form>
      <p className="mt-6 text-sm text-zinc-400">{translateLabel("Already have an account?")} <Link to={searchParams.get("returnTo") ? `/login?returnTo=${encodeURIComponent(normalizeAuthReturnTo(searchParams.get("returnTo")))}` : "/login"} className="font-bold text-[#CCFF00] hover:underline" data-testid="go-login">{translateLabel("Sign in")}</Link></p>
    </AuthShell>
  );
}


export function VerifyEmail() {
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");
  const started = useRef(false);
  const [state, setState] = useState({ status: "loading", message: translateLabel("Verifying your email…") });

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) {
      setState({ status: "error", message: translateLabel("This verification link is missing its token.") });
      return;
    }

    let active = true;
    api.post("/auth/verify-email", { token })
      .then(async ({ data }) => {
        if (!active) return;
        try { await refreshUser(); } catch {}
        setState({
          status: data.verified ? "success" : "error",
          message: data.verified ? "Your email is verified." : "Email verification could not be completed.",
        });
      })
      .catch((error) => {
        if (!active) return;
        setState({
          status: "error",
          message: formatApiErrorDetail(error.response?.data?.detail) || error.message || "Verification failed.",
        });
      });

    return () => { active = false; };
  }, [refreshUser, token]);

  return (
    <AuthShell
      title={state.status === "success" ? translateLabel("Email verified") : state.status === "error" ? translateLabel("Verification failed") : translateLabel("Verifying email")}
      subtitle={state.message}
    >
      <div className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-5">
        {state.status === "loading" && <p className="text-sm text-zinc-300">{translateLabel("Please keep this page open for a moment.")}</p>}
        {state.status === "success" && (
          <Link to="/" className="inline-flex rounded-full bg-[#CCFF00] px-6 py-3 text-sm font-bold text-black">
            {translateLabel("Back to discovery")}
          </Link>
        )}
        {state.status === "error" && (
          <p className="text-sm text-zinc-400">
            Sign in and request a new verification email from your Rider Profile if this link has expired.
          </p>
        )}
      </div>
    </AuthShell>
  );
}
